import {noop} from '@benchling/js-utils';
import {css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {Section} from 'constants/NavConstants';
import {getTabStyles, navBarIconStyles} from 'nav/navbar/NavBarStyles';
import {sectionToReadable} from 'nav/NavUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {snakeToCamelCase} from 'util/NameUtil';
import {ElementMouseEvent} from 'util/Types';

export interface NavBarIconProps {
  className?: string;
  section: Section;
  icon: BenchlingIconName;
  isActive: boolean;
  link?: string;
  onSectionClick?: (section: Section, e?: ElementMouseEvent) => void;
  hasActivity: boolean;
  // When true, the icon opens a dialog (e.g. the omnibox) rather than selecting a sidepanel tab. It
  // then exposes button/dialog ARIA semantics so screen readers announce it accurately, instead of
  // the tab relationship that would otherwise mislead assistive-technology users.
  opensDialog?: boolean;
}

class NavBarIcon extends React.Component<NavBarIconProps> {
  static defaultProps = {
    hasActivity: false,
    isActive: false,
  };

  _handleSectionClick = (e: ElementMouseEvent): void => {
    if (this.props.link && this.props.onSectionClick) {
      // If both a link and click handler are provided, we want the click handler behavior to override
      // the default link action
      e.preventDefault();
    }
    trackEvent(
      {
        module: ANALYTICS_MODULE.NAVIGATION,
        actionCategory: ACTION_CATEGORY.READ,
        action: 'clicked_navbar_icon',
      },
      {section: this.props.section}
    );
    if (this.props.onSectionClick) {
      this.props.onSectionClick(this.props.section, e);
    }
  };

  override render(): JSX.Element {
    const showActivityIndicator = this.props.hasActivity && !this.props.isActive;
    const innerIcon = (
      <div className={css(navBarIconStyles.inner)}>
        <BenchlingIcon
          className={classNames(css(navBarIconStyles.icon), this.props.className)}
          icon={this.props.icon}
          aria-hidden="true"
        />
        {showActivityIndicator && (
          <div
            className={classNames(
              css(navBarIconStyles.activityIndicator),
              '_test-navBarIcon-activityIndicator'
            )}
            {...getTestProps({component: 'NavBarIcon', element: 'activityIndicator'})}
          />
        )}
      </div>
    );
    const dataTestAttrs = {
      component: 'NavBarIcon',
      key: snakeToCamelCase(this.props.section.toLowerCase()),
    };
    const label = sectionToReadable(this.props.section) ?? '';
    const onClick = this.props.link ? noop : this._handleSectionClick;
    const className = css(getTabStyles(this.props.isActive));
    const content = this.props.link ? (
      <a
        href={this.props.link}
        onClick={this._handleSectionClick}
        className={css(navBarIconStyles.iconWrapper)}
        {...getTestProps(dataTestAttrs)}
      >
        {innerIcon}
      </a>
    ) : (
      <div className={css(navBarIconStyles.iconWrapper)} {...getTestProps(dataTestAttrs)}>
        {innerIcon}
      </div>
    );
    return (
      <Tooltip
        isDisabled={this.props.isActive}
        placement={FloatingPlacement.RIGHT}
        text={sectionToReadable(this.props.section)}
      >
        {this.props.opensDialog ? (
          // Screen readers announce a control by its ARIA role, so the role must match what the
          // control actually does. This one opens a dialog (the omnibox), so it's a button that
          // pops up a dialog — not a tab that selects a panel. Announcing tab semantics
          // (role="tab"/aria-controls/aria-selected) would tell assistive-technology users it
          // controls a "SEARCH-panel" that never appears.
          <div
            id={`${this.props.section}-tab`}
            aria-label={label}
            aria-haspopup="dialog"
            role="button"
            onClick={onClick}
            tabIndex={0}
            className={className}
          >
            {content}
          </div>
        ) : (
          <div
            id={`${this.props.section}-tab`}
            aria-controls={`${this.props.section}-panel`}
            aria-label={label}
            aria-selected={this.props.isActive}
            role="tab"
            onClick={onClick}
            tabIndex={0}
            className={className}
          >
            {content}
          </div>
        )}
      </Tooltip>
    );
  }
}

export default NavBarIcon;
